import { useState, useEffect, useRef } from 'react';

export default function Contact() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: 'Orçamentos',
    message: '',
    agreement: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.agreement) {
      alert('Por favor, aceite a Política de Dados e Privacidade.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d5nrg07ecauet1j8b9k0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          subject: 'Orçamentos',
          message: '',
          agreement: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    });
  };

  return (
    <section ref={sectionRef} id="contato" className="py-12 md:py-16 lg:py-24 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-start">
          {/* Map */}
          <div className={`rounded-xl overflow-hidden shadow-xl h-80 lg:h-full lg:min-h-[500px] transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <iframe
              title="Avenida Marcos Matiussi Cúrcio de Souza, 1141, Hortolândia 13183878"
              src="https://maps.google.com/maps?q=Avenida%20Marcos%20Matiussi%20C%C3%BArcio%20de%20Souza%2C%201141%2C%20Hortol%C3%A2ndia%2013183878&t=m&z=15&output=embed&iwloc=near"
              className="w-full h-full"
              allowFullScreen
            ></iframe>
          </div>

          {/* Contact Form */}
          <div className={`bg-white rounded-xl shadow-xl p-6 md:p-8 lg:p-10 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="mb-6 md:mb-8">
              <span className="text-xs md:text-sm font-semibold text-gray-500 uppercase tracking-wide">
                Nosso atendimento
              </span>
              <div className="h-1 w-12 md:w-16 bg-[#C0C0C0] mt-2 mb-4 md:mb-6"></div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
                Entre em contato
              </h2>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-4">
                Temos como objetivo atender bem para atender sempre, buscando a satisfação e a realização de nossos clientes do fechamento da casa até os acabamentos.
              </p>
              
              {/* Contact Info */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-3 text-gray-700">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#C0C0C0]/10 rounded-lg">
                    <i className="ri-mail-line text-lg text-[#C0C0C0]"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Email</p>
                    <a href="mailto:alumiinnova@gmail.com" className="text-sm md:text-base font-semibold hover:text-[#C0C0C0] transition-colors">
                      alumiinnova@gmail.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 text-gray-700">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#C0C0C0]/10 rounded-lg">
                    <i className="ri-phone-line text-lg text-[#C0C0C0]"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Telefone</p>
                    <a href="https://wa.me/5519740982245" className="text-sm md:text-base font-semibold hover:text-[#C0C0C0] transition-colors">
                      (19) 7409-8245
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="grid md:grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Nome"
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:outline-none focus:border-[#C0C0C0] transition-colors duration-300"
                />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Telefone"
                  required
                  pattern="[0-9()#&+*-=.]+"
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:outline-none focus:border-[#C0C0C0] transition-colors duration-300"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="E-mail"
                  required
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:outline-none focus:border-[#C0C0C0] transition-colors duration-300"
                />
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:outline-none focus:border-[#C0C0C0] transition-colors duration-300 bg-white"
                >
                  <option value="Orçamentos">Orçamentos</option>
                  <option value="Dúvidas">Dúvidas</option>
                  <option value="Parcerias">Parcerias</option>
                  <option value="Outros Assuntos">Outros Assuntos</option>
                </select>
              </div>

              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Mensagem"
                rows={4}
                maxLength={500}
                className="w-full px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:outline-none focus:border-[#C0C0C0] transition-colors duration-300 mb-3 md:mb-4 resize-none"
              ></textarea>

              <div className="mb-4 md:mb-6">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="agreement"
                    checked={formData.agreement}
                    onChange={handleChange}
                    required
                    className="mt-1 w-4 h-4 md:w-5 md:h-5 text-[#C0C0C0] border-gray-300 rounded focus:ring-[#C0C0C0]"
                  />
                  <span className="text-xs md:text-sm text-gray-600">
                    Assinale aqui se você leu e está de acordo com a nossa Política de Dados e Privacidade.
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#C0C0C0] hover:bg-[#A8A8A8] text-[#010E31] font-semibold px-6 md:px-8 py-3 md:py-3.5 text-sm md:text-base rounded-lg transition-all duration-300 inline-flex items-center justify-center space-x-2 whitespace-nowrap disabled:opacity-50"
              >
                <span>{isSubmitting ? 'Enviando...' : 'Enviar Formulário'}</span>
                <i className="ri-arrow-right-line"></i>
              </button>

              {submitStatus === 'success' && (
                <p className="mt-3 md:mt-4 text-sm md:text-base text-green-600 text-center font-medium">
                  Mensagem enviada com sucesso!
                </p>
              )}
              {submitStatus === 'error' && (
                <p className="mt-3 md:mt-4 text-sm md:text-base text-red-600 text-center font-medium">
                  Erro ao enviar mensagem. Tente novamente.
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
